const STORAGE_KEY = "yard_pending_check_in_trailers";

function readRaw() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeRaw(entries) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-40)));
}

/** Normalize gate direction labels to grid/API enums. */
export function normalizeShipmentDirection(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const v = raw.toUpperCase();
  if (v === "INBOUND" || v.startsWith("INBOUND")) return "INBOUND";
  if (v === "OUTBOUND" || v.startsWith("OUTBOUND")) return "OUTBOUND";
  if (v === "IN" || v === "INBOUND") return "INBOUND";
  if (v === "OUT" || v === "OUTBOUND") return "OUTBOUND";
  if (raw === "Inbound") return "INBOUND";
  if (raw === "Outbound") return "OUTBOUND";
  return null;
}

export function isOutboundShipmentDirection(value) {
  return normalizeShipmentDirection(value) === "OUTBOUND";
}

/** True if shipment is outbound (empty trailer); false = inbound (loaded). */
export function isOutboundShipment(appointment) {
  if (!appointment || typeof appointment !== "object") return false;

  const fromDirection = normalizeShipmentDirection(
    appointment.shipmentType ??
      appointment.shipmentDirection ??
      appointment.direction
  );
  if (fromDirection) return fromDirection === "OUTBOUND";

  const load = String(appointment.loadType ?? "").trim().toUpperCase();
  if (load === "OUTBOUND" || load.startsWith("OUTBOUND")) return true;
  if (load === "INBOUND" || load.startsWith("INBOUND")) return false;

  return false;
}

export function trailerStatusForCheckIn(appointment) {
  return isOutboundShipment(appointment) ? "EMPTY" : "LOADED";
}

/**
 * Grid row shape aligned with TrailersTab /api/trailers/grid usage.
 * `id` may be null until backend persists — View is disabled in that case.
 *
 * @param {string} [shipmentDirection] Optional `INBOUND` | `OUTBOUND` from check-in form — drives Loaded vs Empty for the grid.
 */
export function buildTrailerGridRowFromCheckIn({
  appointment,
  trailerNumber,
  carrierName,
  locationLabel,
  shipmentNumberOverride,
  shipmentDirection,
}) {
  const normalizedDirection =
    normalizeShipmentDirection(shipmentDirection) ??
    normalizeShipmentDirection(
      appointment?.shipmentType ??
        appointment?.shipmentDirection ??
        appointment?.direction
    ) ??
    (isOutboundShipment(appointment) ? "OUTBOUND" : "INBOUND");
  const inbound = normalizedDirection === "INBOUND";
  const shipmentType = inbound ? "INBOUND" : "OUTBOUND";
  const tn = (trailerNumber || "").trim();
  const carrier = (carrierName || "").trim();
  const shipNo =
    (shipmentNumberOverride && String(shipmentNumberOverride).trim()) ||
    appointment?.shipmentNumber ||
    null;

  const trailerDisplay =
    tn ||
    (appointment?.appointmentId != null
      ? `Pending-${appointment.appointmentId}`
      : shipNo
        ? `Pending-${shipNo}`
        : "—");

  return {
    id: appointment?.trailerId ?? appointment?.trailer_id ?? null,
    trailerNumber: trailerDisplay,
    status: inbound ? "LOADED" : "EMPTY",
    gateStatus: "CHECKED_IN",
    shipmentType,
    carrierName: carrier || "—",
    location: (locationLabel || "").trim() || "—",
    shipmentNumber: shipNo,
    appointmentNumber:
      appointment?.appointmentNumber ??
      appointment?.appointmentId ??
      null,
    movable: true,
    moveBlockedReason: null,
    _fromCheckIn: true,
  };
}

export function recordPendingCheckInTrailerRow(row) {
  const num = (row?.trailerNumber || "").trim();
  if (!num || num === "—") return;
  const entries = readRaw().filter(
    (e) => e?.row?.trailerNumber !== row.trailerNumber
  );
  entries.push({ at: Date.now(), row });
  writeRaw(entries);
}

function pendingRowsOnly() {
  return readRaw().map((e) => e.row).filter(Boolean);
}

/** Normalize trailer # for duplicate checks (case-insensitive, trimmed). */
export function normalizeTrailerNumberForComparison(value) {
  return String(value ?? "").trim().toUpperCase();
}

/** Pending grid rows from session (not merged / mutated with API). */
export function getPendingCheckInRows() {
  return pendingRowsOnly();
}

/**
 * Align grid row shipmentType with the canonical INBOUND/OUTBOUND enum for display.
 * Does NOT touch `status` — Condition is a real, independently-editable trailer field (see
 * TrailerDetailView's "Save Condition"), not something derivable from shipment direction alone.
 * An earlier version of this function force-reset status to the direction's default (Empty for
 * Outbound, Loaded for Inbound) on every grid load, silently reverting any manual Condition
 * change — including the very checkout-blocking rule ("outbound trailer can't check out while
 * still Empty") that requires that change to actually stick.
 */
export function normalizeTrailerGridRow(row) {
  if (!row || typeof row !== "object") return row;
  const direction = normalizeShipmentDirection(
    row.shipmentType ?? row.shipmentDirection ?? row.direction
  );
  if (!direction) return row;
  return {
    ...row,
    shipmentType: direction,
  };
}

/**
 * Merge API grid rows with pending check-in rows not yet returned by the API.
 * Drops pending entries once the same trailer number appears in `apiRows`.
 */
export function mergePendingCheckInTrailers(apiRows) {
  const list = (Array.isArray(apiRows) ? apiRows : []).map(normalizeTrailerGridRow);
  const apiNums = new Set(
    list.map((t) => (t?.trailerNumber || "").trim()).filter(Boolean)
  );

  const pending = pendingRowsOnly();
  const pendingByNum = new Map(
    pending.map((p) => [normalizeTrailerNumberForComparison(p.trailerNumber), p])
  );

  const mergedApi = list.map((apiRow) => {
    const pendingRow = pendingByNum.get(
      normalizeTrailerNumberForComparison(apiRow.trailerNumber)
    );
    if (pendingRow?._fromCheckIn && pendingRow.shipmentType) {
      // Once the trailer has a real API row, the API's own status is always more current than
      // the check-in-time snapshot — a later Condition change (or checkout) makes the pending
      // value wrong, and there's no expiry on it, so it silently masks the true status forever.
      // Only shipmentType is still backfilled here, for the brief window right after check-in
      // where the grid endpoint may not have caught up yet.
      return normalizeTrailerGridRow({
        ...apiRow,
        shipmentType: pendingRow.shipmentType,
      });
    }
    return apiRow;
  });

  const extras = pending.filter(
    (p) => (p?.trailerNumber || "").trim() && !apiNums.has(p.trailerNumber.trim())
  );

  const remaining = readRaw().filter(
    (e) => !apiNums.has((e?.row?.trailerNumber || "").trim())
  );
  writeRaw(remaining);

  return [...extras, ...mergedApi];
}
